import { v5 as uuidv5 } from 'uuid';

import ApiError from '~/types/ApiError';
import type { CppPartner } from '~/types/costPerPoint';
import type { AdminPartnerCreditBody } from '~/types/loyaltyContract';

import { formatDecimal } from '~/utils/numberFormatters';

export const MAX_PARTNER_EARN_CSV_ROWS = 500;

export type PartnerEarnCreditField = 'basePoints' | 'statusCredits' | 'activityTitle' | 'reason';

export type PartnerEarnCreditValues = Record<PartnerEarnCreditField, string>;

export type PartnerEarnCreditErrors = Partial<Record<PartnerEarnCreditField | 'form', string>>;

export const EMPTY_PARTNER_EARN_CREDIT_VALUES: PartnerEarnCreditValues = {
  basePoints: '',
  statusCredits: '',
  activityTitle: '',
  reason: '',
};

export function isEarnEligiblePartner(partner: CppPartner): boolean {
  return partner.status === 'ACTIVE' && partner.allowCreditPoints;
}

export function partnerLimitsLabel(partner: CppPartner): string {
  const pointsRange = `${formatDecimal(partner.minPointsPerTransaction)}–${formatDecimal(partner.maxPointsPerTransaction)} pts`;
  if (!partner.allowCreditStatusCredits) {
    return pointsRange;
  }
  return `${pointsRange}, ${formatDecimal(partner.minStatusCreditsPerTransaction)}–${formatDecimal(partner.maxStatusCreditsPerTransaction)} SC`;
}

export function parseOptionalInteger(
  raw: string,
): { kind: 'empty' } | { kind: 'invalid' } | { kind: 'value'; value: number } {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { kind: 'empty' };
  }

  if (!/^-?\d+$/.test(trimmed)) {
    return { kind: 'invalid' };
  }

  const value = Number(trimmed);
  if (!Number.isSafeInteger(value)) {
    return { kind: 'invalid' };
  }

  return { kind: 'value', value };
}

function rangeError(value: number, min: number, max: number, label: string): string | undefined {
  if (value < min || value > max) {
    return `${label} must be between ${min.toLocaleString('en-AU')} and ${max.toLocaleString('en-AU')}`;
  }
  return undefined;
}

export function validatePartnerEarnCredit(
  values: PartnerEarnCreditValues,
  partner: CppPartner,
): PartnerEarnCreditErrors {
  const errors: PartnerEarnCreditErrors = {};
  const baseParsed = parseOptionalInteger(values.basePoints);
  const statusParsed = parseOptionalInteger(values.statusCredits);

  if (baseParsed.kind === 'invalid') {
    errors.basePoints = 'Enter a whole number';
  } else if (baseParsed.kind === 'value') {
    const range = rangeError(
      baseParsed.value,
      partner.minPointsPerTransaction,
      partner.maxPointsPerTransaction,
      'Base points',
    );
    if (range) {
      errors.basePoints = range;
    }
  }

  if (statusParsed.kind === 'invalid') {
    errors.statusCredits = 'Enter a whole number';
  } else if (statusParsed.kind === 'value') {
    if (!partner.allowCreditStatusCredits) {
      errors.statusCredits = 'This partner cannot credit status credits';
    } else {
      const range = rangeError(
        statusParsed.value,
        partner.minStatusCreditsPerTransaction,
        partner.maxStatusCreditsPerTransaction,
        'Status credits',
      );
      if (range) {
        errors.statusCredits = range;
      }
    }
  }

  if (!values.activityTitle.trim()) {
    errors.activityTitle = 'Activity title is required';
  }

  if (!values.reason.trim()) {
    errors.reason = 'Reason is required';
  }

  const hasBase = baseParsed.kind === 'value';
  const hasStatus = statusParsed.kind === 'value';
  if (!hasBase && !hasStatus) {
    errors.form = partner.allowCreditStatusCredits ? 'Enter base points or status credits' : 'Enter base points';
  }

  return errors;
}

export function buildPartnerEarnCreditBody(
  accountNumber: number,
  values: PartnerEarnCreditValues,
  partner: CppPartner,
  sourceKey?: string,
): Omit<AdminPartnerCreditBody, 'idempotencyKey'> | undefined {
  const errors = validatePartnerEarnCredit(values, partner);
  if (Object.keys(errors).length > 0) {
    return undefined;
  }

  const baseParsed = parseOptionalInteger(values.basePoints);
  const statusParsed = parseOptionalInteger(values.statusCredits);

  return {
    accountNumber,
    activityTitle: values.activityTitle.trim(),
    ...(baseParsed.kind === 'value' ? { basePoints: baseParsed.value } : {}),
    ...(statusParsed.kind === 'value' ? { statusCredits: statusParsed.value } : {}),
    source: {
      reason: values.reason.trim(),
      ...(sourceKey ? { key: sourceKey } : {}),
    },
  };
}

export function buildPartnerEarnIdempotencyKey(
  partnerId: string,
  body: Omit<AdminPartnerCreditBody, 'idempotencyKey'>,
): string {
  return uuidv5(
    [
      partnerId,
      String(body.accountNumber),
      body.activityTitle,
      String(body.basePoints ?? 0),
      String(body.statusCredits ?? 0),
      body.source.key ?? '',
    ].join('\0'),
    uuidv5.DNS,
  );
}

export function isDuplicatePartnerEarnCredit(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}
